/**
 * 导航页账密种子 —— 与仓库根 index.html（五端导航页）联动。
 * ---------------------------------------------------------------------------
 * 背景：导航页与各端是不同 origin，localStorage 按 origin 隔离，
 * 导航页写不了 admin.bluplanet.cn 的存储 → 靠链接 hash 送种子：
 *   跳转地址 = <登录页>#c=<base64(JSON({u,p}))>
 * 落地时由 App 级 consumeNavSeed() 消费：写入本端 key 并立即清除 hash
 * （fragment 不进服务器日志/Referer；base64 仅防肉眼，非加密）。
 * 登录页从本端 key 读回显（记住我含密码，便利优先，仅存本机）。
 */

const KEY = 'bp_nav_creds_admin';

export interface NavCreds {
  u: string;
  p: string;
}

function b64ToJson(b64: string): NavCreds | null {
  try {
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const obj = JSON.parse(new TextDecoder().decode(bytes)) as { u?: unknown; p?: unknown };
    if (typeof obj.u !== 'string' || typeof obj.p !== 'string') return null;
    return { u: obj.u, p: obj.p };
  } catch {
    return null;
  }
}

/** 读取并消费 hash 种子（读后立即清 hash，防泄露/防刷新重复带入）；无种子返回 null */
export function consumeNavSeed(): NavCreds | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.hash.match(/#c=([A-Za-z0-9+/=]+)/);
  if (!m) return null;
  const creds = b64ToJson(m[1]);
  // 无论解析成败都清掉 hash，避免脏种子残留在地址栏/历史里
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return creds && (creds.u || creds.p) ? creds : null;
}

/** 记住我：保存账号+密码（便利优先，仅本机 localStorage 可读） */
export function saveNavCreds(u: string, p: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ u, p }));
  } catch {
    /* ignore */
  }
}

/** 退出登录降级：保留用户名回显、清除密码（共用设备/换账号不残留） */
export function saveNavCredsUsernameOnly(u: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ u, p: '' }));
  } catch {
    /* ignore */
  }
}

export function loadNavCreds(): NavCreds | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw) as { u?: unknown; p?: unknown };
    if (typeof obj?.u !== 'string' || !obj.u) return null;
    return { u: obj.u, p: typeof obj.p === 'string' ? obj.p : '' };
  } catch {
    return null;
  }
}

export function clearNavCreds(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
